import { Link } from "react-router-dom";
import { ScrollVideo } from "@/components/ScrollVideo";
import { Reveal } from "@/components/Reveal";
import { ProductImage } from "@/components/ProductImage";
import { useProducts } from "@/lib/useProducts";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";
import { VIDEO, POSTER } from "@/assets";

const T = {
  fr: {
    seoTitle: "Crisopea — la voie du feu · Maison Partita",
    seoDesc: "Sept eaux de parfum, une par métal de l'alchimie, chacune sous sa planète et son glyphe. La collection suit l'ordre du Grand Œuvre.",
    kicker: "La voie du feu",
    intro: "Sept eaux de parfum, une par métal de l'alchimie, chacune sous sa planète et son glyphe. La collection suit l'ordre du Grand Œuvre, du plomb de Saturne jusqu'à l'or du Soleil. On arrache : il faut de la chaleur, de la pression, de la force brute.",
    format: "Eau de parfum concentrée · 50 ml · Série limitée numérotée.",
    film: "Film · La voie du feu",
    collection: "La Collection",
    soldOut: "Épuisé",
    altEtui: (roman, metal, planet) => `Étui CRISOPEA ${roman}, ${metal}, glyphe de ${planet}`,
  },
  en: {
    seoTitle: "Crisopea — the way of fire · Maison Partita",
    seoDesc: "Seven eaux de parfum, one for each metal of alchemy, each under its planet and its glyph. The collection follows the order of the Great Work.",
    kicker: "The way of fire",
    intro: "Seven eaux de parfum, one for each metal of alchemy, each under its planet and its glyph. The collection follows the order of the Great Work, from Saturn's lead to the Sun's gold. It is wrested into being: heat, pressure, raw force.",
    format: "Concentrated eau de parfum · 50 ml · Numbered limited series.",
    film: "Film · The way of fire",
    collection: "The Collection",
    soldOut: "Sold out",
    altEtui: (roman, metal, planet) => `CRISOPEA ${roman} case, ${metal}, glyph of ${planet}`,
  },
};

export default function Crisopea() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const { products } = useProducts("crisopea");

  return (
    <div>
      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 pb-24">
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase" style={{ color: "#B08D4A" }}>{t.kicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-[0.1em] uppercase text-zinc-50">Crisopea</h1>
          <p className="mt-10 text-[20px] leading-[1.8] text-zinc-300">{t.intro}</p>
          <p className="mt-6 text-[17px] text-zinc-500">{t.format}</p>
        </Reveal>
      </section>

      <ScrollVideo src={VIDEO.crisopea} poster={POSTER.crisopea} heightVh={180}>
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-10 flex items-end pb-16">
          <p className="text-[13px] tracking-[0.34em] uppercase" style={{ color: "#B08D4A" }}>{t.film}</p>
        </div>
      </ScrollVideo>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 pb-32">
        <Reveal><p className="text-[13px] tracking-[0.34em] uppercase text-zinc-500 mb-16">{t.collection}</p></Reveal>
        <div className="grid gap-x-10 gap-y-20 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.12}>
              <Link to={lp(lang, `/crisopea/${p.slug}`)} data-testid={`collection-${p.slug}`} className="group block">
                <div className="overflow-hidden bg-zinc-900 border border-zinc-800">
                  <ProductImage maison="crisopea" reference={p.reference} suffix="etui"
                    alt={t.altEtui(p.roman, loc(p, lang, "metal"), loc(p, lang, "planet"))}
                    className={`transition-opacity duration-[1000ms] ${p.available ? "opacity-100" : "opacity-70 group-hover:opacity-90"}`} />
                </div>
                <div className="mt-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-xl tracking-[0.2em] uppercase text-zinc-100">CRISOPEA {p.roman}</h3>
                    <span className="text-2xl" style={{ color: p.color }} aria-hidden="true">{p.glyph}</span>
                  </div>
                  <p className="mt-1 text-zinc-500 tracking-[0.08em]">{loc(p, lang, "metal")} · {loc(p, lang, "planet")}</p>
                  {p.available
                    ? <p className="mt-2 text-zinc-300">{p.price} €</p>
                    : <p className="mt-2 text-[13px] tracking-[0.2em] uppercase text-zinc-600">{t.soldOut}</p>}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
